import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function GeneralPhotosDialog({ open, onClose, budget, onUpdate }) {
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const photos = budget?.general_photos || [];

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const uploaded = [];
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (r.data?.path) uploaded.push(r.data.path);
      }
      if (uploaded.length) {
        const r = await api.post(`/budgets/${budget.id}/general-photos`, { paths: uploaded });
        toast.success(`${uploaded.length} foto(s) adicionada(s)`);
        onUpdate?.(r.data.general_photos);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar fotos");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (path) => {
    if (!window.confirm("Remover esta foto?")) return;
    try {
      const r = await api.delete(`/budgets/${budget.id}/general-photos`, { params: { path } });
      toast.success("Foto removida");
      onUpdate?.(r.data.general_photos);
    } catch (e) { toast.error("Erro ao remover"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-violet-600" />
            Fotos do orçamento Nº {budget?.number}
          </DialogTitle>
          <DialogDescription>
            Fotos gerais do serviço (fachadas, locais, documentos). A escola consegue visualizar essas fotos no portal, mesmo antes do orçamento ser finalizado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => upload(Array.from(e.target.files || []))}
              data-testid="input-general-photos"
            />
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => upload(Array.from(e.target.files || []))}
              data-testid="input-general-camera"
            />
            <Button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="btn-upload-general-photos"
            >
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</> : <><Upload className="w-4 h-4 mr-2" /> Da galeria</>}
            </Button>
            <Button
              onClick={() => cameraRef.current?.click()}
              disabled={uploading}
              variant="outline"
              className="border-violet-200 text-[#7c3aed] hover:bg-violet-50"
              data-testid="btn-upload-general-camera"
            >
              📷 Tirar foto
            </Button>
          </div>

          {photos.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
              Nenhuma foto geral neste orçamento ainda.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {photos.map((p, idx) => {
                const src = `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;
                return (
                  <div key={p + idx} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                    <a href={src} target="_blank" rel="noreferrer" className="block w-full h-full">
                      <img src={src} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                    </a>
                    <button
                      type="button"
                      onClick={() => remove(p)}
                      className="absolute top-1 right-1 w-7 h-7 rounded-full bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`btn-remove-general-${idx}`}
                      aria-label="Remover foto"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
